import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getCountFromServer, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import { useAuthStore } from '../stores/authStore';
import { ONLINE_THRESHOLD_MINUTES } from '../utils/time';

interface Stats {
  totalUsers: number;
  onlineUsers: number;
  botUsers: number;
  totalMessages: number;
  todayMessages: number;
  totalDMs: number;
  totalReports: number;
  pendingReports: number;
}

export default function DashboardPage() {
  const { adminUser } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  async function loadStats() {
    try {
      const usersCol = collection(db, 'users');
      const msgsCol = collection(db, 'messages');
      const dmsCol = collection(db, 'directMessages');
      const reportsCol = collection(db, 'reports');

      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const onlineThreshold = Timestamp.fromDate(new Date(Date.now() - ONLINE_THRESHOLD_MINUTES * 60 * 1000));

      const [
        totalUsersSnap,
        onlineSnap,
        botSnap,
        totalMsgsSnap,
        todayMsgsSnap,
        totalDMsSnap,
        totalReportsSnap,
        pendingReportsSnap,
      ] = await Promise.all([
        getCountFromServer(usersCol),
        getCountFromServer(query(usersCol, where('lastActiveAt', '>=', onlineThreshold))),
        getCountFromServer(query(usersCol, where('isBot', '==', true))),
        getCountFromServer(msgsCol),
        getCountFromServer(query(msgsCol, where('timestamp', '>=', Timestamp.fromDate(todayStart)))),
        getCountFromServer(dmsCol),
        getCountFromServer(reportsCol),
        getCountFromServer(query(reportsCol, where('status', '==', 'pending'))),
      ]);

      setStats({
        totalUsers: totalUsersSnap.data().count,
        onlineUsers: onlineSnap.data().count,
        botUsers: botSnap.data().count,
        totalMessages: totalMsgsSnap.data().count,
        todayMessages: todayMsgsSnap.data().count,
        totalDMs: totalDMsSnap.data().count,
        totalReports: totalReportsSnap.data().count,
        pendingReports: pendingReportsSnap.data().count,
      });
    } catch (err) {
      console.error('Stats load error:', err);
    } finally {
      setLoading(false);
    }
  }

  const cards = stats ? [
    { label: '전체 사용자', value: stats.totalUsers, color: 'bg-blue-500', to: '/users', state: { filter: 'all' } },
    { label: '온라인 사용자', value: stats.onlineUsers, color: 'bg-green-500', to: '/users', state: { filter: 'online' } },
    { label: '봇 사용자', value: stats.botUsers, color: 'bg-gray-500', to: '/bot', state: { tab: 'monitor' } },
    { label: '전체 메시지', value: stats.totalMessages, color: 'bg-purple-500', to: '/messages?view=all', state: undefined },
    { label: '오늘 메시지', value: stats.todayMessages, color: 'bg-indigo-500', to: '/messages?view=today', state: undefined },
    { label: '전체 쪽지', value: stats.totalDMs, color: 'bg-pink-500', to: '/dm', state: undefined },
    { label: '신고', value: stats.totalReports, sub: stats.pendingReports > 0 ? `미처리 ${stats.pendingReports}` : undefined, color: 'bg-red-500', to: '/reports', state: undefined },
  ] : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <button onClick={loadStats} className="px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          새로고침
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500">통계 로딩 중...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map(card => (
            <button
              key={card.label}
              onClick={() => navigate(card.to, { state: card.state })}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 text-left hover:border-blue-300 hover:shadow-md transition cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className={`w-3 h-3 rounded-full ${card.color}`} />
                <span className="text-sm text-gray-500">{card.label}</span>
              </div>
              <div className="text-3xl font-bold text-gray-900 mt-2">
                {card.value.toLocaleString()}
              </div>
              {'sub' in card && card.sub && (
                <div className="text-xs text-red-500 font-medium mt-1">{card.sub}</div>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <h2 className="text-lg font-semibold mb-4">관리자 정보</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-500">닉네임:</span> <span className="font-medium">{adminUser?.nickname}</span></div>
          <div><span className="text-gray-500">이메일:</span> <span className="font-medium">{adminUser?.email}</span></div>
          <div><span className="text-gray-500">UID:</span> <span className="font-mono text-xs">{adminUser?.uid}</span></div>
          <div><span className="text-gray-500">권한:</span> <span className="font-medium">{adminUser?.isAdmin ? '관리자' : '운영자'}</span></div>
        </div>
      </div>
    </div>
  );
}
